import { useState, useEffect, useMemo } from 'react'
import { useFiltersStore } from '@/store/filters'
import {
  Filter,
  RefreshCw,
  Calendar,
  Clock,
  ChevronDown,
  AlertCircle,
  ArrowDownRight,
  Wifi,
  Smartphone,
  TrendingDown,
  BarChart3,
  Info
} from 'lucide-react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar
} from 'recharts'
import { biApi } from '../lib/api'

// Types
interface FunnelOperator {
  country: string
  operator: string
  totalCampaigns: number
  hasFunnelConfig: boolean
  funnelName: string | null
  supportsHESegmentation: boolean
}

interface FunnelStage {
  id: string
  name: string
  description: string
  color: string
  count: number
  percentage: number
  dropRate: number
  heCount: number | null
  wifiCount: number | null
}

interface FunnelError {
  errorCode: string
  count: number
  sampleMessage: string | null
}

interface TimeSeriesPoint {
  date: string
  clicks: number
  lpDisplay: number
  buttonClick: number
  conversions: number
}

interface FunnelData {
  meta: {
    country: string
    operator: string
    funnelName: string
    startDate: string
    endDate: string
    timezone: string
    connectionTypeFilter: string
    supportsHESegmentation: boolean
    lastUpdated: string
  }
  summary: {
    totalClicks: number
    totalConversions: number
    overallConversionRate: number
    biggestDropStage: string
  }
  stages: FunnelStage[]
  errors: FunnelError[]
  timeSeries: TimeSeriesPoint[]
}

// Timezone options
const TIMEZONE_OPTIONS = [
  { value: 'America/Costa_Rica', label: 'Costa Rica (GMT-6)' },
  { value: 'America/Bogota', label: 'Colombia (GMT-5)' },
  { value: 'America/Mexico_City', label: 'México (GMT-6)' },
  { value: 'America/Lima', label: 'Perú (GMT-5)' },
  { value: 'America/Santiago', label: 'Chile (GMT-4)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (GMT-3)' },
  { value: 'UTC', label: 'UTC' }
]

// Connection type options
const CONNECTION_OPTIONS = [
  { value: 'ALL', label: 'Todos' },
  { value: 'HE', label: 'Header Enrichment (HE)' },
  { value: 'WIFI', label: 'WiFi' }
]

// Filter option types
interface FilterCustomer {
  id: number
  name: string
  shortName?: string
}

interface FilterProduct {
  id: number
  name: string
  reference?: string
  customerId: number
  customerName?: string
}

interface FilterReference {
  reference: string
  productId: number
  productName?: string
  customerName?: string
}

interface FilterOptions {
  customers: FilterCustomer[]
  products: FilterProduct[]
  references: FilterReference[]
  sources: string[]
}

export default function Funnel() {
  // Use global store for filter persistence
  const { 
    filters, 
    funnelFilters, 
    setFilter, 
    setDateRange,
    setFunnelFilter, 
    setFunnelFilters 
  } = useFiltersStore()
  
  // Destructure for convenience
  const { startDate, endDate, timezone } = filters
  const { 
    selectedCountry, 
    selectedOperator, 
    connectionType, 
    selectedCustomer, 
    selectedProduct, 
    selectedReference,
    selectedSource 
  } = funnelFilters
  
  // Local state for data and UI
  const [operators, setOperators] = useState<FunnelOperator[]>([])
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    customers: [],
    products: [],
    references: [],
    sources: []
  })
  const [loadingFilters, setLoadingFilters] = useState(false)
  
  const [funnelData, setFunnelData] = useState<FunnelData | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingOperators, setLoadingOperators] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Derived: unique countries and operators for selected country
  const availableCountries = useMemo(() => {
    const countries = [...new Set(operators.filter(o => o.hasFunnelConfig).map(o => o.country))]
    return countries.sort()
  }, [operators])

  const availableOperators = useMemo(() => {
    return operators.filter(o => o.country === selectedCountry && o.hasFunnelConfig)
  }, [operators, selectedCountry])

  // Prepare HE vs WIFI comparison data
  const heWifiComparison = useMemo(() => {
    if (!funnelData) return []
    return funnelData.stages
      .filter(s => s.heCount !== null && s.wifiCount !== null)
      .map(stage => ({
        name: stage.name,
        HE: stage.heCount,
        WIFI: stage.wifiCount
      }))
  }, [funnelData])

  // Filter customers by products available (products are already filtered by country/operator from backend)
  const availableCustomers = useMemo(() => {
    // Get unique customer IDs from products (which are already filtered by country/operator)
    const customerIdsWithProducts = new Set(
      filterOptions.products
        .filter(p => p.customerId)
        .map(p => p.customerId)
    )
    // Filter customers to only those that have products in the selected country/operator
    return filterOptions.customers.filter(c => customerIdsWithProducts.has(c.id))
  }, [filterOptions.customers, filterOptions.products])

  // Filter products by selected customer
  const availableProducts = useMemo(() => {
    if (selectedCustomer === 'ALL') {
      return filterOptions.products
    }
    return filterOptions.products.filter(p => p.customerId === Number(selectedCustomer))
  }, [filterOptions.products, selectedCustomer])

  // Filter references by selected customer/product
  const availableReferences = useMemo(() => {
    let refs = filterOptions.references
    if (selectedCustomer !== 'ALL') {
      const customerProductIds = filterOptions.products
        .filter(p => p.customerId === Number(selectedCustomer))
        .map(p => p.id)
      refs = refs.filter(r => customerProductIds.includes(r.productId))
    }
    if (selectedProduct !== 'ALL') {
      refs = refs.filter(r => r.productId === Number(selectedProduct))
    }
    return refs
  }, [filterOptions.references, filterOptions.products, selectedCustomer, selectedProduct])

  // Load filter options when country/operator changes
  useEffect(() => {
    const loadFilters = async () => {
      if (!selectedCountry || !selectedOperator) return
      
      try {
        setLoadingFilters(true)
        const response = await biApi.getFunnelFilters({
          country: selectedCountry,
          operator: selectedOperator
        })
        if (response.data.success) {
          setFilterOptions({
            ...response.data.data,
            sources: response.data.data.sources || []
          })
        }
      } catch (err) {
        console.error('Error loading filters:', err)
      } finally {
        setLoadingFilters(false)
      }
    }
    loadFilters()
    // Reset filter selections when country/operator changes
    setFunnelFilters({
      selectedCustomer: 'ALL',
      selectedProduct: 'ALL',
      selectedReference: 'ALL',
      selectedSource: 'ALL'
    })
  }, [selectedCountry, selectedOperator, setFunnelFilters])

  // Load operators on mount
  useEffect(() => {
    const loadOperators = async () => {
      try {
        setLoadingOperators(true)
        const response = await biApi.getFunnelOperators()
        if (response.data.success) {
          setOperators(response.data.data.operators)
          // Auto-select first available funnel config only if not already selected
          const configured = response.data.data.operators.filter((o: FunnelOperator) => o.hasFunnelConfig)
          if (configured.length > 0 && !selectedCountry) {
            setFunnelFilters({
              selectedCountry: configured[0].country,
              selectedOperator: configured[0].operator
            })
          }
        }
      } catch (err) {
        console.error('Error loading operators:', err)
        setError('Error cargando operadores')
      } finally {
        setLoadingOperators(false)
      }
    }
    loadOperators()
  }, [selectedCountry, setFunnelFilters])

  // Load funnel data when filters change
  useEffect(() => {
    if (selectedCountry && selectedOperator) {
      loadFunnelData()
    }
  }, [selectedCountry, selectedOperator])

  const loadFunnelData = async () => {
    if (!selectedCountry || !selectedOperator) return

    setLoading(true)
    setError(null)

    try {
      const response = await biApi.getFunnelAnalysis({
        country: selectedCountry,
        operator: selectedOperator,
        startDate,
        endDate,
        connectionType,
        timezone,
        customerId: selectedCustomer,
        productId: selectedProduct,
        reference: selectedReference,
        source: selectedSource
      })

      if (response.data.success) {
        setFunnelData(response.data.data)
      } else {
        setError(response.data.error || 'Error desconocido')
      }
    } catch (err: any) {
      console.error('Error loading funnel:', err)
      setError(err.response?.data?.error || 'Error cargando datos del funnel')
    } finally {
      setLoading(false)
    }
  }

  if (loadingOperators) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Filter className="w-6 h-6 text-primary-500" />
            Análisis de Funnel
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Visualiza el embudo de conversión por operador
          </p>
        </div>

        <button
          onClick={loadFunnelData}
          disabled={loading || !selectedCountry || !selectedOperator}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              País
            </label>
            <div className="relative">
              <select
                value={selectedCountry}
                onChange={(e) => {
                  setFunnelFilters({
                    selectedCountry: e.target.value,
                    selectedOperator: ''
                  })
                }}
                className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg appearance-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
              >
                <option value="">Seleccionar...</option>
                {availableCountries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Operator */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Operador
            </label>
            <div className="relative">
              <select
                value={selectedOperator}
                onChange={(e) => setFunnelFilter('selectedOperator', e.target.value)}
                disabled={!selectedCountry}
                className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg appearance-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white disabled:opacity-50"
              >
                <option value="">Seleccionar...</option>
                {availableOperators.map(op => (
                  <option key={op.operator} value={op.operator}>{op.operator}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Calendar className="inline w-4 h-4 mr-1" />
              Fecha Inicio
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setDateRange(e.target.value, endDate)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Calendar className="inline w-4 h-4 mr-1" />
              Fecha Fin
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setDateRange(startDate, e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
            />
          </div>

          {/* Connection Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Smartphone className="inline w-4 h-4 mr-1" />
              Conexión
            </label>
            <div className="relative">
              <select
                value={connectionType}
                onChange={(e) => setFunnelFilter('connectionType', e.target.value as 'ALL' | 'HE' | 'WIFI')}
                className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg appearance-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
              >
                {CONNECTION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Clock className="inline w-4 h-4 mr-1" />
              Zona Horaria
            </label>
            <div className="relative">
              <select
                value={timezone}
                onChange={(e) => setFilter('timezone', e.target.value)}
                className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg appearance-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
              >
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Second row: Source, Customer, Product, Reference filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {/* Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Source
            </label>
            <div className="relative">
              <select
                value={selectedSource}
                onChange={(e) => setFunnelFilter('selectedSource', e.target.value)}
                disabled={loadingFilters}
                className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg appearance-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white disabled:opacity-50"
              >
                <option value="ALL">Todos los sources</option>
                {filterOptions.sources.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Customer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cliente
            </label>
            <div className="relative">
              <select
                value={selectedCustomer}
                onChange={(e) => {
                  setFunnelFilters({
                    selectedCustomer: e.target.value,
                    selectedProduct: 'ALL',
                    selectedReference: 'ALL'
                  })
                }}
                disabled={loadingFilters}
                className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg appearance-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white disabled:opacity-50"
              >
                <option value="ALL">Todos los clientes</option>
                {availableCustomers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Product */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Producto
            </label>
            <div className="relative">
              <select
                value={selectedProduct}
                onChange={(e) => {
                  setFunnelFilters({
                    selectedProduct: e.target.value,
                    selectedReference: 'ALL'
                  })
                }}
                disabled={loadingFilters}
                className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg appearance-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white disabled:opacity-50"
              >
                <option value="ALL">Todos los productos</option>
                {availableProducts.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.customerName ? `(${p.customerName})` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Reference (External Product ID) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reference (ID Externo)
            </label>
            <div className="relative">
              <select
                value={selectedReference}
                onChange={(e) => setFunnelFilter('selectedReference', e.target.value)}
                disabled={loadingFilters || availableReferences.length === 0}
                className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg appearance-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white disabled:opacity-50"
              >
                <option value="ALL">Todas las referencias</option>
                {availableReferences.map(r => (
                  <option key={`${r.reference}-${r.productId}`} value={r.reference}>
                    {r.reference} {r.productName ? `- ${r.productName}` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* No Config Available */}
      {availableCountries.length === 0 && !loadingOperators && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
          <Info className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-amber-800 dark:text-amber-200">
            No hay configuraciones de funnel disponibles
          </h3>
          <p className="text-amber-600 dark:text-amber-400 mt-2">
            Contacta al administrador para habilitar el análisis de funnel para tus operadores.
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      )}

      {/* Funnel Data */}
      {funnelData && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Total Clicks</span>
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 text-orange-500" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                {funnelData.summary.totalClicks.toLocaleString()}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Conversiones</span>
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <Filter className="w-4 h-4 text-green-500" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                {funnelData.summary.totalConversions.toLocaleString()}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Tasa de Conversión</span>
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-4 h-4 text-blue-500 rotate-180" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                {funnelData.summary.overallConversionRate.toFixed(2)}%
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Mayor Abandono</span>
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <ArrowDownRight className="w-4 h-4 text-red-500" />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white mt-2 truncate">
                {funnelData.summary.biggestDropStage}
              </p>
            </div>
          </div>

          {/* Funnel Visualization */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Filter className="w-5 h-5 text-primary-500" />
              {funnelData.meta.funnelName}
            </h2>

            {/* Funnel Stages */}
            <div className="space-y-4">
              {funnelData.stages.map((stage, index) => (
                <div key={stage.id} className="relative">
                  <div className="flex items-center gap-4">
                    {/* Stage number */}
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                      style={{ backgroundColor: stage.color }}
                    >
                      {index + 1}
                    </div>

                    {/* Stage info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {stage.name}
                        </span>
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-bold text-gray-900 dark:text-white">
                            {stage.count.toLocaleString()}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            ({stage.percentage.toFixed(1)}%)
                          </span>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                        <div
                          className="h-full rounded-lg transition-all duration-500"
                          style={{
                            width: `${Math.max(stage.percentage, 2)}%`,
                            backgroundColor: stage.color
                          }}
                        />
                      </div>

                      {/* HE/WIFI breakdown if available */}
                      {stage.heCount !== null && stage.wifiCount !== null && (
                        <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Smartphone className="w-3 h-3" />
                            HE: {stage.heCount.toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Wifi className="w-3 h-3" />
                            WIFI: {stage.wifiCount.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Drop rate indicator */}
                  {index > 0 && stage.dropRate > 0 && (
                    <div className="absolute -top-3 left-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs px-2 py-0.5 rounded">
                      <ArrowDownRight className="w-3 h-3 inline mr-1" />
                      -{stage.dropRate.toFixed(1)}% drop
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* HE vs WIFI Comparison (if supported) */}
          {funnelData.meta.supportsHESegmentation && heWifiComparison.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-primary-500" />
                Comparación HE vs WIFI
              </h2>

              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={heWifiComparison} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis type="number" tick={{ fill: '#9CA3AF' }} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fill: '#9CA3AF' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="HE" fill="#f97316" name="Header Enrichment" />
                    <Bar dataKey="WIFI" fill="#3b82f6" name="WiFi" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Time Series */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-500" />
              Evolución Diaria
            </h2>

            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={funnelData.timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('es', { day: '2-digit', month: 'short' })}
                  />
                  <YAxis tick={{ fill: '#9CA3AF' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                    labelFormatter={(value) => new Date(value).toLocaleDateString('es', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="clicks" stroke="#f97316" name="Clicks" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="lpDisplay" stroke="#fb923c" name="LP Display" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="buttonClick" stroke="#fdba74" name="Button Click" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="conversions" stroke="#22c55e" name="Conversiones" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Errors Table */}
          {funnelData.errors.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Errores Detectados
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Código
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Cantidad
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Mensaje de Ejemplo
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnelData.errors.map((error, idx) => (
                      <tr key={idx} className="border-b border-gray-100 dark:border-gray-700/50">
                        <td className="py-3 px-4 font-mono text-sm text-red-600 dark:text-red-400">
                          {error.errorCode}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-gray-900 dark:text-white">
                          {error.count.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 truncate max-w-xs">
                          {error.sampleMessage || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Meta Info */}
          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 justify-end">
            <Clock className="w-4 h-4" />
            Última actualización: {new Date(funnelData.meta.lastUpdated).toLocaleString('es')}
          </div>
        </>
      )}
    </div>
  )
}
